import {
  AppliedPromotion,
  CheckoutData,
  StaffCheckoutIntent,
  TablePartialCheckout,
} from "@lemmonhq/types";
import { calculateCartSubtotal } from "./cart";
import { calculateOrderItemTotal } from "./orders";
import { round } from "../utils/index";

/**
 * Reward types that attach to specific products (they carry `eligible_items`),
 * as opposed to `order_discount` which reduces the whole order with no item
 * anchor. Only these can be split along item lines — a happy-hour
 * (`special_price`) discount follows the leg that collects the discounted item.
 * See docs/plans/checkout_editor/11_promotion_aware_split.md.
 */
const ITEM_SCOPED_REWARD_TYPES: ReadonlySet<AppliedPromotion["reward_type"]> =
  new Set(["special_price", "product_discount", "product"]);

/** Whether a promotion attaches to specific products (see above). */
export const isItemScopedPromotion = (promotion: AppliedPromotion): boolean =>
  ITEM_SCOPED_REWARD_TYPES.has(promotion.reward_type);

/**
 * Quantities the armed leg collects, keyed by cart line (`cartId`). Only a
 * partial leg selects specific items; a split (equal/amount) leg carries none,
 * so this returns an empty map for it.
 */
const legQuantitiesByCartId = (
  intent: StaffCheckoutIntent | undefined,
): Map<string, number> => {
  const map = new Map<string, number>();
  if (intent?.mode === "partial") {
    for (const item of intent.items) {
      map.set(item.cartId, (map.get(item.cartId) ?? 0) + item.quantity);
    }
  }
  return map;
};

/**
 * One promotion's value scoped to the currently-armed leg.
 *
 * Item-scoped promotion on a leg that selected specific items: attach the value
 * to the eligible units THIS leg collects, capping each eligible line at the
 * leg's collected quantity (`min(eligibleQty, legQty)`) — the same cap
 * `accounting_post_sale_journal` applies via `LEAST(elig_qty, item_qty)`, so the
 * amount we charge the leg equals what the ledger attributes to it. A leg that
 * collects none of the eligible items gets zero of the promotion.
 *
 * Everything else (order-level promotion, or a split/amount leg with no item
 * selection) falls back to proportional-by-amount — matching the ledger's
 * `proportional_gross` fallback.
 */
const legScopedPromotionValue = (
  promotion: AppliedPromotion,
  legQuantities: Map<string, number>,
  proportionalFactor: number,
): number => {
  const canScopeByItems =
    isItemScopedPromotion(promotion) &&
    legQuantities.size > 0 &&
    promotion.eligible_items.length > 0 &&
    promotion.eligible_items.every((item) => item.cart_item_id != null);

  if (!canScopeByItems) {
    return round(promotion.value * proportionalFactor);
  }

  const totalEligibleQty = promotion.eligible_items.reduce(
    (sum, item) => sum + item.quantity,
    0,
  );
  if (totalEligibleQty <= 0) {
    return round(promotion.value * proportionalFactor);
  }

  let collectedEligibleQty = 0;
  for (const item of promotion.eligible_items) {
    const legQty = legQuantities.get(item.cart_item_id!) ?? 0;
    collectedEligibleQty += Math.min(item.quantity, legQty);
  }
  return round(promotion.value * (collectedEligibleQty / totalEligibleQty));
};

export const calculateTipAmount = (tip?: { value: number }): number => {
  return tip?.value || 0;
};

export const calculatePartialSubTotal = (
  partial: NonNullable<TablePartialCheckout["items"]>,
): number => {
  return Object.values(partial).reduce((sum, item) => sum + item.total, 0);
};

export const calculatePromotionsTotal = (checkout: CheckoutData) => {
  return checkout.promotions
    ? {
        total: checkout.promotions.reduce((s, p) => s + p.value, 0),
        promotions: checkout.promotions,
      }
    : null;
};

/**
 * Order-wide promotions scaled to the currently-armed split/partial leg.
 *
 * The promotion engine computes rewards against the WHOLE cart, but a staged
 * leg only pays for its share. This returns the leg's rewards payload — a
 * `total` plus every application with its `value` scoped to the leg — so the
 * leg's payment record and its captured promotion applications reflect only the
 * payer's portion. With no leg armed it returns the full order rewards
 * unchanged; with no promotions it returns null (callers pass that straight
 * through as "no rewards").
 *
 * Item-scoped promotions (happy-hour `special_price`, `product_discount`) are
 * scoped by the leg's own item selection (see `legScopedPromotionValue`);
 * order-level promotions and split/amount legs fall back to proportional-by-
 * amount. Both mirror `accounting_post_sale_journal`, so the leg charge equals
 * the ledger attribution.
 *
 * Single source of truth for both transports: the cash/manual path (client)
 * and the integrated path (server `createPaymentRecordForAsyncTransaction`)
 * both call this so a card leg can never over-apply the whole-cart promotion.
 * See docs/plans/checkout_editor/11_promotion_aware_split.md.
 */
export const calculateLegScopedRewards = (
  checkout: CheckoutData,
): { total: number; promotions: NonNullable<CheckoutData["promotions"]> } | null => {
  const promotions = checkout.promotions;
  if (!promotions || promotions.length === 0) return null;

  const whole = promotions.reduce((s, p) => s + p.value, 0);
  const intent = checkout.staffCheckout?.intent;
  if (!intent || whole === 0) {
    return { total: round(whole), promotions };
  }

  const subtotal = calculateCheckoutSubTotal(checkout);
  const factor = subtotal > 0 ? intent.amount / subtotal : 0;
  const legQuantities = legQuantitiesByCartId(intent);

  // Drop applications that scope to nothing on this leg (e.g. a happy-hour item
  // this leg didn't collect) so the payment doesn't capture a meaningless
  // zero-value promotion row.
  const scaled = promotions
    .map((p) => ({ ...p, value: legScopedPromotionValue(p, legQuantities, factor) }))
    .filter((p) => p.value !== 0);
  const total = scaled.reduce((s, p) => s + p.value, 0);
  return { total: round(total), promotions: scaled };
};

/**
 * Promotions total scoped to the currently-armed split/partial leg — the sum of
 * `calculateLegScopedRewards`. With no leg armed it returns the full order
 * promotions total; with no promotions it returns 0.
 */
export const calculateLegScopedPromotionsTotal = (
  checkout: CheckoutData,
): number => {
  return calculateLegScopedRewards(checkout)?.total ?? 0;
};

export const calculateCheckoutSubTotal = (checkout: CheckoutData) => {
  if (!checkout.payload) return 0;

  let subtotal = 0;
  if (checkout.payload.type === "cart") {
    subtotal = calculateCartSubtotal(checkout.payload.object);
  } else {
    // For table checkout
    //console.log("calculateCheckoutSubTotal", checkout.payload.object);
    if (checkout.payload.object.type === "partial") {
      // If partial payment, use the partial totals
      subtotal = calculatePartialSubTotal(checkout.payload.object.items);
    } else if (checkout.payload.object.type === "split") {
      return checkout.payload.object.splitAmount;
    } else {
      // Otherwise sum all order amounts
      const items = checkout.payload.object.orders
        .map((el) => el.items || [])
        .flat();

      subtotal = items.reduce(
        (sum, item) => sum + calculateOrderItemTotal(item),
        0,
      );
      return subtotal;
    }
  }
  return subtotal;
};

/**
 * The amount to actually charge for the current checkout ACTION.
 *
 * For a staged split/partial leg of a staff_checkout (`staffCheckout.intent`
 * armed) this is the leg's NET amount — the same figure the payment row records
 * (see `createPaymentRecordForAsyncTransaction` / `create_payment.sql`): the
 * gross leg amount minus the leg-scoped discount / promotions / gift cards,
 * floored at 0, plus the tip. Returning the raw `intent.amount` (gross) would
 * charge the provider a different figure than the ledger stores — e.g. a CHF 50
 * leg with a CHF 10 discount would be charged 50 but booked 40, and a tip would
 * be charged short. Provider adapters (terminal / SoftPOS) MUST use this, never
 * `calculateCheckoutTotal`. With no leg armed it falls back to the whole-
 * checkout total.
 */
export const calculateCheckoutChargeAmount = (
  checkout: CheckoutData,
): number => {
  const staged = getStaffCheckoutStagedLeg(checkout);
  if (!staged) return calculateCheckoutTotal(checkout);
  const intent = staged.intent;

  const discountAmount = checkout.discount?.amount || 0;
  const promotionsAmount = calculateLegScopedRewards(checkout)?.total ?? 0;
  const giftCardAmount = (checkout.giftCards || []).reduce(
    (s, g) => s + g.amount,
    0,
  );
  const tipAmount = calculateTipAmount(checkout.tip);
  const net = Math.max(
    0,
    intent.amount - discountAmount - promotionsAmount - giftCardAmount,
  );
  return round(net + tipAmount);
};

export const calculateCheckoutTotal = (checkout: CheckoutData): number => {
  const subtotal = calculateCheckoutSubTotal(checkout);

  const discountAmount = checkout.discount?.amount || 0;
  const tipAmount = calculateTipAmount(checkout.tip);
  const promotionsAmount = checkout.promotions
    ? checkout.promotions.reduce((s, p) => s + p.value, 0)
    : 0;
  const deliveryFee =
    checkout.payload?.type === "cart" &&
    checkout.payload?.object.deliveryFee?.value
      ? checkout.payload?.object.deliveryFee?.value
      : 0;
  //  console.log(
  //    JSON.stringify({
  //      subtotal,
  //      discountAmount,
  //      promotionsAmount,
  //      tipAmount,
  //      deliveryFee,
  //    }),
  //  );

  const giftCardAmount = checkout.giftCards
    ? checkout.giftCards.reduce((s, g) => s + g.amount, 0)
    : 0;

  // Price reductions (discount + promotions) can never take the merchandise
  // value below zero — a percentage discount stacked on a promotion, an
  // over-large fixed discount, or overlapping promotions would otherwise show a
  // negative total (and charge a negative amount). Mirror create_payment.sql:
  // floor the merchandise net, add delivery, apply gift-card tender (also
  // floored), then add the tip.
  const merchandiseNet = Math.max(0, subtotal - discountAmount - promotionsAmount);
  const payable = Math.max(
    0,
    merchandiseNet +
      deliveryFee -
      giftCardAmount -
      calculateTableCreditAmount(checkout),
  );
  return round(payable + tipAmount);
};

/**
 * The table credit a table checkout spends: as much of what is left to pay
 * (after discount, promotions and gift cards) as the table's credit covers.
 * Never the tip. The server holds/takes exactly this amount
 * (`p_table_credit_amount`, docs/plans/table_credit/credit_hold_at_checkout.md).
 */
export const calculateTableCreditAmount = (checkout: CheckoutData): number => {
  const available = checkout.tableCredit?.available ?? 0;
  if (!(available > 0) || checkout.payload?.type !== "table") return 0;

  const subtotal = calculateCheckoutSubTotal(checkout);
  const discountAmount = checkout.discount?.amount || 0;
  const promotionsAmount = checkout.promotions
    ? checkout.promotions.reduce((s, p) => s + p.value, 0)
    : 0;
  const giftCardAmount = checkout.giftCards
    ? checkout.giftCards.reduce((s, g) => s + g.amount, 0)
    : 0;
  const due = Math.max(
    0,
    Math.max(0, subtotal - discountAmount - promotionsAmount) - giftCardAmount,
  );
  return round(Math.min(available, due));
};

/**
 * Table credit pays everything this checkout collects (no tip on top), so
 * whichever payment method the cashier picks, the payment is the credit alone.
 */
export const isPaidByTableCredit = (checkout: CheckoutData): boolean =>
  calculateTableCreditAmount(checkout) > 0 &&
  calculateCheckoutTotal(checkout) <= 0.005;

/**
 * The idempotency key for one staged split/partial leg.
 *
 * Keyed on the checkout plus the leg SLOT (how many legs are already
 * collected), so the same leg retried after a lost response reuses its key and
 * is recognised as a replay, while the next leg gets a fresh one.
 *
 * Both transports call this — the cash/manual client path and the integrated
 * path via `generateCheckoutIdempotencyKeys` — so a card leg and a cash leg
 * can never key differently. Before this, cart checkouts keyed on the POS
 * checkout id alone: every leg of a bill shared one key on the integrated path,
 * and the cash path minted a random key per attempt (so retries never matched).
 *
 * NOTE: this only takes effect once create_payment's payment_idempotency_keys
 * INSERT is restored — it is commented out today, which is why the staged
 * functions also carry their own `p_expected_leg_count` replay guard.
 */
export const staffCheckoutLegIdempotencyKey = (
  staffCheckoutId: string,
  collectedLegCount: number,
): string => `staff_checkout_${staffCheckoutId}_leg_${collectedLegCount}`;

/**
 * The staged split/partial leg this checkout is currently collecting, or null
 * if it is an ordinary whole-cart payment. Returns the checkout id alongside
 * the leg so callers get both without re-narrowing.
 *
 * BOTH conditions matter. `staffCheckout.id` alone is NOT enough: a lifecycle
 * row is opened for EVERY cart checkout (see the tRPC `staffCheckouts.create`
 * called on CheckoutComponent mount), so the id is present on a plain full
 * payment that was never split. Only `intent` — set when the cashier arms a leg
 * in the split editor — means "this payment is one of several".
 *
 * Every branch that decides "is this a staged leg?" must use this, or they
 * drift: keying on the id alone makes an ordinary integrated (terminal /
 * SoftPOS / online) payment look like a split leg, so it is captured, parked at
 * `confirmed`, and never turned into an order.
 */
export const getStaffCheckoutStagedLeg = (
  checkout: CheckoutData,
): {
  id: string;
  intent: NonNullable<NonNullable<CheckoutData["staffCheckout"]>["intent"]>;
} | null => {
  const sc = checkout.staffCheckout;
  return sc?.id && sc.intent ? { id: sc.id, intent: sc.intent } : null;
};
